import type { ClientStreamStatsMessage, CongestionMessage } from '@playcastdotio/PeerCore';
import {
  RECOVERY_HEALTHY_HOLD_MS,
  RttFloorTracker,
  TARGET_FRAMERATE,
  classifyLinkImpairment,
  isDebugWebrtcLoggingEnabled,
  type SessionDowngradeFloor,
  type StickyFloorEncoding,
  type StreamEncodeContext,
  type StreamHealthEvent,
} from '@playcastdotio/PeerCore';
import { sinkWebrtcDebugLine } from './webrtcDebugSink';
import { ConnectSettleGate } from './ConnectSettleGate';
import { DecodeLagDowngradeManager } from './DecodeLagDowngradeManager';
import { LatencyBudgetActionManager } from './LatencyBudgetActionManager';
import {
  RecoveryCapacityGate,
  type RecoveryHostCapacityStats,
  type RecoveryLever,
} from './RecoveryCapacityGate';
import { RecoveryClimbManager } from './RecoveryClimbManager';
import { restoredFloorEpisode, stickyFloorEncodingFor, type RecoveryCutContext } from './RecoveryCutPolicy';
import { RecoveryEntryManager, type DeepeningBlock } from './RecoveryEntryManager';
import { RecoveryEpisodeStore, type PostClearReadiness, type RecoverySnapshot } from './RecoveryEpisodeStore';
import { RecoveryVerdictRouter } from './RecoveryVerdictRouter';
import { StickyFloorReclaimManager } from './StickyFloorReclaimManager';
import { MobileRecoveryBinding } from './mobileRecovery/MobileRecoveryBinding';
import { hardEpisode } from './RecoveryCutPolicy';
import { RecoveryClientEvidence } from './mobileRecovery/adaptationHardening/RecoveryClientEvidence';
import { ContinuousHealthEvidence } from './mobileRecovery/adaptationHardening/ContinuousHealthEvidence';
import {
  HostWindowTransitionGuard,
  linkProvesCongestion,
  type HostWindowLinkSample,
} from './HostWindowTransitionGuard';

export type {
  PeerRecovery,
  PostClearReadiness,
  RecoveryCutKind,
  RecoverySnapshot,
  RecoveryState,
} from './RecoveryEpisodeStore';

export type { CongestionRecoveryDeps } from './mobileRecovery/adaptationHardening/RecoveryDependencies';
import type { CongestionRecoveryDeps } from './mobileRecovery/adaptationHardening/RecoveryDependencies';

/** Priming result for a new or reclaimed peer connection under a sticky floor. */
export type StickyPrimingEncoding = StickyFloorEncoding & {
  /** True when startup burst above the floor must be skipped. */
  skipBurst: boolean;
  /** True when a sticky floor is active for this peer. */
  floored: boolean;
};

/** Recovery orchestration; focused managers own entry, exit, capacity, and reclaim policy. */
export class CongestionRecoveryManager {
  private readonly episodes: RecoveryEpisodeStore;
  private readonly entry: RecoveryEntryManager;
  private readonly climb: RecoveryClimbManager;
  private readonly router: RecoveryVerdictRouter;
  private readonly decodeLag: DecodeLagDowngradeManager;
  private readonly budget: LatencyBudgetActionManager | null;
  private readonly reclaim: StickyFloorReclaimManager | null;
  private readonly settle: ConnectSettleGate;
  private readonly capacity = new RecoveryCapacityGate();
  private readonly mobileBindings = new Map<string, MobileRecoveryBinding>();
  private readonly handoffCaps = new Map<string, number>();
  private readonly rttFloor: RttFloorTracker;
  private readonly clientEvidence: RecoveryClientEvidence;
  private readonly hostContinuity = new Map<string, ContinuousHealthEvidence>();
  private readonly windowGuard = new HostWindowTransitionGuard();
  private readonly knownPeers = new Set<string>();
  private readonly lastLink = new Map<string, HostWindowLinkSample>();
  private bindingGeneration = 0;
  private readonly deps: CongestionRecoveryDeps;
  private readonly nowMs: () => number;

  constructor(deps: CongestionRecoveryDeps) {
    this.deps = deps;
    this.nowMs = deps.nowMs ?? (() => Date.now());
    this.clientEvidence = new RecoveryClientEvidence({ nowMs: this.nowMs,
      stale: peerId => { this.capacity.invalidateGuest(peerId); this.invalidatePlaybackEvidence(peerId); },
      emit: (peerId, event) => this.emitHealth(peerId, event), debug: (tag, detail) => this.debug(tag, detail) });
    this.rttFloor = new RttFloorTracker({ nowMs: this.nowMs });
    this.settle = new ConnectSettleGate({ nowMs: this.nowMs });
    const emitHealth = (peerId: string, event: StreamHealthEvent) => this.emitHealth(peerId, event);
    const debug = (tag: string, line: Record<string, unknown>) => this.debug(tag, line);
    const deepeningBlock = (peerId: string, bypassSettle = false) =>
      this.deepeningBlock(peerId, bypassSettle, 'bitrate-enter');
    const cutContextFor = (peerId: string) => this.cutContextFor(peerId);
    this.episodes = new RecoveryEpisodeStore({
      applyEncoding: (peerId, encoding) => {
        if (deps.runtimePolicy?.snapshot(peerId).mode !== 'fixed-application') deps.applyEncoding(peerId,
          { ...encoding, maxBitrateBps: Math.min(encoding.maxBitrateBps, this.handoffCaps.get(peerId) ?? Infinity) });
      },
      stickyDowngrade: deps.stickyDowngrade,
      qualityLedger: deps.qualityLedger,
    });
    this.entry = new RecoveryEntryManager({
      unhealthyHoldMs: peerId => deps.runtimePolicy?.snapshot(peerId).unhealthyHoldMs,
      episodes: this.episodes,
      cutContextFor,
      stickyDowngrade: deps.stickyDowngrade,
      deepeningBlock,
      onFloorRaised: (peerId, floor, reason) => this.onFloorRaised(peerId, floor, reason),
      debug,
      nowMs: this.nowMs,
    });
    this.decodeLag = new DecodeLagDowngradeManager({
      getCurrentTier: deps.getCurrentTier,
      getCurrentFps: deps.getCurrentFps,
      applyDecodeLagTierChange: deps.applyDecodeLagTierChange,
      stickyDowngrade: deps.stickyDowngrade,
      raiseStickyFloor: (peerId, floor, reason) => this.entry.raiseStickyFloor(peerId, floor, reason),
      // bitrate-enter, NOT tier-demotion: the decode-lag path calls this only after its own
      // standingBuffer check has failed, so under the capacity gate a non-tier-demotion lever
      // means settle-only. The sustained-capacity path inside the same manager is
      // pre-authorized by the caller via allows('tier-demotion', ...) and its re-check here
      // passes for any state that earned that authorization.
      deepeningBlock: (peerId) => this.deepeningBlock(peerId, false, 'bitrate-enter'),
      applyCapacityTierDemotion: deps.applyCapacityTierDemotion,
      resolutionFallbackUnavailableReason: deps.resolutionFallbackUnavailableReason,
      emitHealth,
      debug,
      nowMs: this.nowMs,
    });
    this.climb = new RecoveryClimbManager({
      healthyHoldMs: peerId => deps.runtimePolicy?.snapshot(peerId).healthyHoldMs,
      episodes: this.episodes,
      requestKeyframeOnce: (peerId) => deps.requestKeyframeOnce(peerId, 'recovery-climb'),
      cutContextFor,
      stickyFloorFor: (peerId) => this.stickyFloorFor(peerId),
      floorBlocksClimb: (peerId) => this.mobileBindings.get(peerId)?.blocksReclaim() === true || (deps.stickyDowngrade?.blocksClimb(peerId) ?? false),
      // The climb IDR is withheld while a decode-lag episode holds — but an episode built on
      // guest evidence that stopped flowing must not withhold anything: null while stale.
      decodeLagEpisodeFor: (peerId) => (this.clientEvidence.isStale(peerId) ? null : this.decodeLag.episodeFor(peerId)),
      nowMs: this.nowMs,
    });
    this.router = new RecoveryVerdictRouter({
      entry: this.entry,
      climb: this.climb,
      isNormal: (peerId) => this.episodes.isNormal(peerId),
      deepeningBlock,
      getSignalAvailability: deps.getSignalAvailability,
      onWirelessLossEnded: (peerId, framesDropped) =>
        deps.requestKeyframeOnce(peerId, 'loss-burst-end', framesDropped),
      debug,
      nowMs: this.nowMs,
    });
    this.budget = deps.latencyBudget
      ? new LatencyBudgetActionManager({
          latencyBudget: deps.latencyBudget,
          getCalibratedLatency: deps.getCalibratedLatency,
          getStandingLatency: deps.getStandingLatency,
          deepeningBlock: (peerId) => this.deepeningBlock(peerId, false, 'latency-budget'),
          emitHealth,
          correct: (peerId) => this.correctLatencyBreach(peerId),
          debug,
          nowMs: this.nowMs,
        })
      : null;
    this.reclaim = deps.stickyDowngrade
      ? new StickyFloorReclaimManager({
          stickyDowngrade: deps.stickyDowngrade,
          getCalibratedLatency: deps.getCalibratedLatency,
          getStandingLatency: deps.getStandingLatency,
          isDraining: deps.isDraining,
          requestKeyframeOnce: (peerId) => deps.requestKeyframeOnce(peerId, 'floor-release'),
          onReleased: (peerId, from, to, reason) => this.onFloorReleased(peerId, from, to, reason),
          nowMs: this.nowMs,
        })
      : null;
  }

  /** Marks the point from which this peer's silence counts as idleness (first call wins). */
  public beginActivityWatch(peerId: string): void {
    this.climb.beginActivityWatch(peerId);
  }

  /** Records player activity; never resets the climb. */
  public onPlayerActivity(peerId: string): void {
    this.climb.onPlayerActivity(peerId);
  }

  /** Accept measured excess queueing, or derive it from RTT after trusting this peer's floor. */
  public onLocalStats(
    peerId: string,
    stats: RecoveryHostCapacityStats,
  ): void {
    this.knownPeers.add(peerId);
    const now = this.nowMs();
    let continuity = this.hostContinuity.get(peerId);
    if (!continuity) { continuity = new ContinuousHealthEvidence(); this.hostContinuity.set(peerId, continuity); }
    if (!continuity.observe(now)) this.climb.invalidateTransportEvidence(peerId);
    this.clientEvidence.check(peerId);
    if (this.mobileBindings.get(peerId)?.blocksReclaim()) this.invalidatePlaybackEvidence(peerId);
    // sendDelayMs is ignored — pacer delay must not enter/escalate recovery.
    const standingQueueMs = stats.standingQueueMs !== undefined
      ? stats.standingQueueMs
      : typeof stats.rttMs === 'number'
        ? this.rttFloor.standingQueueMs(peerId, stats.rttMs)
        : null;
    this.capacity.observeHostStats(peerId, this.nowMs(), standingQueueMs, stats);
    this.rememberLink(peerId, { lossPct: stats.packetsLostPct, standingQueueMs, lossDelta: stats.packetsLostDelta });
    this.mobileFor(peerId).onHostCapacity({ ...stats, standingQueueMs });
    // A large loss count is congestion unless the round trip is still on its floor.
    const flatLoss = classifyLinkImpairment({
      packetsLostPct: stats.packetsLostPct,
      standingQueueMs,
      ...(typeof standingQueueMs === 'number' || typeof stats.rttMs !== 'number' ? {} : { rttMs: stats.rttMs }),
    }) === 'wireless-loss';
    if (stats.packetsLostDelta !== undefined && stats.packetsLostDelta > 50 && !flatLoss) {
      this.climb.invalidateTransportEvidence(peerId);
      this.entry.enterOrEscalate(peerId, 'local-loss', /* bypassSettle */ true);
      this.syncBitrateCutEligibility(peerId);
      return;
    }
    this.router.ingest(
      peerId,
      { standingQueueMs, packetsLostPct: stats.packetsLostPct },
      'host',
      undefined,
      undefined,
      stats.rttMs,
    );
    this.syncBitrateCutEligibility(peerId);
  }

  /** Guest measurements share host thresholds; 'unchanged' marks healthy normal-state reports. */
  public onClientStats(
    peerId: string,
    msg: ClientStreamStatsMessage,
  ): 'critical' | 'elevated' | 'none' | 'insufficient-data' | 'unchanged' | void {
    this.knownPeers.add(peerId);
    this.rememberLink(peerId, { lossPct: msg.packetsLostPct, standingQueueMs: msg.standingQueueMs });
    const mobile = this.mobileFor(peerId);
    const bounds = msg.sampledAtEpochMs === undefined ? undefined
      : this.deps.guestWallTimeBounds?.(peerId, msg.sampledAtEpochMs, msg.recoveryEvidence?.path?.epoch ?? 0);
    this.clientEvidence.observe(peerId, msg);
    const mobileDecision = mobile.onClientStats(msg, this.nowMs(), bounds);
    const unknownPlayback = mobileDecision?.state === 'unknown' || mobileDecision?.state === 'suspended';
    if (unknownPlayback) this.invalidatePlaybackEvidence(peerId);
    if (mobileDecision && !mobile.hasFreshTransportEvidence()) {
      this.capacity.invalidateGuest(peerId);
      return 'insufficient-data';
    }
    // Fresh transport measurements remain actionable without presentation authority.
    const controlMessage = unknownPlayback ? { type: 'clientStats' as const,
      packetsLostPct: msg.packetsLostPct, standingQueueMs: msg.standingQueueMs } : msg;
    this.settle.noteFirstStats(peerId);
    this.capacity.observeGuestMessage(
      peerId,
      this.nowMs(),
      controlMessage,
      this.deps.getSignalAvailability?.(peerId),
      this.deps.getSignalQualityLevel?.(peerId) ?? undefined,
    );
    // Every fps judgement is against what the encoder MEASURABLY sent THIS peer: field data
    // showed a host at 17fps encode calling its guest's 18fps decode a 40% deficit every tick.
    const deliveredFps = this.deliveredFpsFor(peerId);
    const standingVerdict = this.deps.getStandingLatency?.(peerId);
    const standingBuffer = standingVerdict?.kind === 'standing-buffer' && standingVerdict.evidenceState === 'confirmed';
    if (!unknownPlayback && !mobile.hasQualifiedCapacity()) this.decodeLag.observe(peerId, msg, deliveredFps, standingBuffer);
    if (!unknownPlayback) this.budget?.observe(peerId);
    const sample = this.router.guestSample(peerId, controlMessage, deliveredFps);
    // PRODUCT DECISION B: the climb gate reads the calibrated reading + floor and the guest's
    // decode FPS. The guest's RTT is deliberately not fed: it is not glass-to-glass.
    const calibrated = this.deps.getCalibratedLatency?.(peerId);
    if (!unknownPlayback) this.climb.observeGate(
      peerId,
      {
        decodeFps: msg.decodeFps,
        deliveredFps,
        calibratedLatencyMs: calibrated?.lastMs ?? undefined,
        calibratedFloorMs: calibrated?.floorMs ?? undefined,
      },
      sample.verdict === 'none' && !mobile.blocksReclaim(),
    );
    const freezeFrames = sample.inputs.availability?.freeze === false
      ? 0
      : (msg.receiverCounters?.freezes ?? msg.freezeDelta ?? 0);
    this.router.ingest(
      peerId,
      sample.inputs,
      'guest',
      sample.heldTrendMs,
      (msg.receiverCounters?.framesDropped ?? 0) + freezeFrames,
      msg.rttMs,
    );
    this.syncBitrateCutEligibility(peerId);
    // The legacy capacity dwell must not race a qualified modern playback demotion.
    if (!unknownPlayback && !mobile.hasQualifiedCapacity())
      this.decodeLag.observeSustainedCapacity(peerId, this.capacity.allows(peerId, 'tier-demotion', standingBuffer));
    if (!mobile.blocksReclaim()) {
      this.reclaim?.tick(peerId, {
        lossPct: msg.packetsLostPct,
        standingQueueMs: msg.standingQueueMs,
        rttMs: msg.rttMs,
      });
    }
    else this.reclaim?.invalidateEvidence(peerId);
    if (unknownPlayback) return 'insufficient-data';
    if (sample.verdict === 'none' && this.episodes.isNormal(peerId)) return 'unchanged';
    return sample.verdict;
  }

  /** Legacy verdicts are ignored while measured reports flow, preventing double counting. */
  public onCongestionMessage(peerId: string, msg: CongestionMessage): void {
    this.knownPeers.add(peerId);
    if (this.clientEvidence.recentlyMeasured(peerId, RECOVERY_HEALTHY_HOLD_MS)) return;
    if (msg.level === 'critical') {
      // Critical may reflect downstream loss or freezes this host cannot observe.
      this.entry.enterOrEscalate(peerId, 'client-critical');
      this.syncBitrateCutEligibility(peerId);
      return;
    }
    // Legacy clients retain bounded soft-cut re-entry; modern reports suppress this path.
    this.entry.enterElevated(peerId);
    this.syncBitrateCutEligibility(peerId);
  }

  public getSnapshot(peerId: string): RecoverySnapshot {
    const snapshot = this.episodes.snapshot(peerId, {
      maxBitrateBps: this.deps.getProfileMaxBitrateBps(peerId),
      maxFramerate: this.fpsCeilingForPeer(peerId),
    });
    return { ...snapshot, maxBitrateBps: Math.min(snapshot.maxBitrateBps, this.handoffCaps.get(peerId) ?? Infinity) };
  }

  /** Prime new/reclaimed connections under retained bitrate/FPS floors, without a burst. */
  public primingEncodingFor(peerId: string): StickyPrimingEncoding {
    const encoding = this.stickyFloorEncodingFor(peerId);
    const floored = this.stickyFloorFor(peerId) !== 'none' || this.fpsCeilingForPeer(peerId) < TARGET_FRAMERATE;
    return { ...encoding, skipBurst: floored, floored };
  }

  /** Restore the floor's encoding override after rebuild; absent floors are a no-op. */
  public restoreStickyFloorHold(peerId: string): boolean {
    const floor = this.stickyFloorFor(peerId);
    if (floor === 'none') return false;
    const peer = restoredFloorEpisode(floor, this.stickyFloorEncodingFor(peerId), this.nowMs());
    this.episodes.set(peerId, peer);
    this.episodes.apply(peerId, peer);
    this.capacity.noteBitrateCut(peerId);
    return true;
  }

  /** Clear sticky floor + fps ceiling. ONLY on hold-expiry or host-initiated session end. */
  public clearStickySession(peerId: string): void {
    this.deps.stickyDowngrade?.clearPeer(peerId);
    this.reclaim?.clearPeer(peerId);
    try {
      this.deps.qualityLedger?.endEpoch(peerId);
    } catch {
      // Ledger must never take recovery down.
    }
  }

  /** Post-impairment readiness for product-path e2e. IDR alone is not sufficient for PASS. */
  public getPostClearReadiness(peerId: string): PostClearReadiness {
    return this.episodes.postClearFor(peerId);
  }

  public clearPeer(peerId: string): void {
    this.mobileBindings.get(peerId)?.dispose();
    this.mobileBindings.delete(peerId);
    this.handoffCaps.delete(peerId);
    this.episodes.clearPeer(peerId);
    this.climb.clearPeer(peerId);
    this.router.clearPeer(peerId);
    this.decodeLag.clearPeer(peerId);
    this.budget?.clearPeer(peerId);
    this.settle.clearPeer(peerId);
    this.capacity.clearPeer(peerId);
    this.rttFloor.clearPeer(peerId);
    this.clientEvidence.clearPeer(peerId);
    this.hostContinuity.delete(peerId);
    this.knownPeers.delete(peerId);
    this.lastLink.delete(peerId);
    this.reclaim?.invalidateEvidence(peerId);
    // Rebuilds retain protective floors, but need new continuous evidence to release them.
  }

  public clearAll(): void {
    for (const binding of this.mobileBindings.values()) binding.dispose();
    this.mobileBindings.clear();
    this.handoffCaps.clear();
    this.episodes.clearAll();
    this.climb.clearAll();
    this.router.clearAll();
    this.decodeLag.clearAll();
    this.budget?.clearAll();
    this.settle.clearAll();
    this.capacity.clearAll();
    this.rttFloor.clearAll();
    this.clientEvidence.clearAll();
    this.hostContinuity.clear();
    this.knownPeers.clear();
    this.lastLink.clear();
  }

  /**
   * The host window changed show-state. A cut that opened with that stall, on a link that is
   * not actually congested, is removed and the profile cap is written back.
   */
  public noteHostWindowTransition(): void {
    const now = this.nowMs();
    this.windowGuard.note(now);
    for (const peerId of this.knownPeers) this.releaseWindowCut(peerId, now);
  }

  /** Why nothing may deepen for this peer right now, or null when it may. */
  private deepeningBlock(peerId: string, bypassSettle = false, lever: RecoveryLever = 'bitrate-enter'): DeepeningBlock | null {
    if (this.deps.runtimePolicy?.snapshot(peerId).mode === 'fixed-application') return 'settling';
    if (this.windowGuard.holding(this.nowMs())) {
      const proven = bypassSettle && linkProvesCongestion(this.lastLink.get(peerId));
      if (!proven) return 'window-transition';
    }
    if (this.mobileBindings.get(peerId)?.hasQualifiedCapacity()) return null;
    if (!this.settle.settled(peerId, this.calibratedFloorTrusted(peerId), bypassSettle)) return 'settling';
    const verdict = this.deps.getStandingLatency?.(peerId);
    const standingBuffer = verdict?.kind === 'standing-buffer' && verdict.evidenceState === 'confirmed';
    if (!this.capacity.allows(peerId, lever, standingBuffer)) return 'standing-buffer';
    return null;
  }

  public getMobileQualitySnapshot(peerId: string): ReturnType<MobileRecoveryBinding['qualitySnapshot']> & { climbGateLocked: boolean } {
    return { ...this.mobileFor(peerId).qualitySnapshot(), climbGateLocked: this.climb.isGateLocked(peerId) };
  }

  public resetPathEvidence(peerId: string, pathEpoch: number): void {
    this.rttFloor.clearPeer(peerId);
    this.capacity.clearPeer(peerId);
    this.router.clearPeer(peerId);
    this.climb.clearPeer(peerId);
    this.reclaim?.invalidateEvidence(peerId);
    this.hostContinuity.delete(peerId);
    this.deps.onPathChanged?.(peerId, pathEpoch);
  }

  public setHandoffProtection(peerId: string, maxBitrateBps: number | null): void {
    const previous = this.handoffCaps.get(peerId);
    if (maxBitrateBps === null) this.handoffCaps.delete(peerId);
    else this.handoffCaps.set(peerId, maxBitrateBps);
    if (previous === this.handoffCaps.get(peerId)) return;
    const snapshot = this.getSnapshot(peerId);
    this.deps.applyEncoding(peerId, { maxBitrateBps: snapshot.maxBitrateBps, maxFramerate: snapshot.maxFramerate });
  }

  public getHandoffProtection(peerId: string): number | undefined { return this.handoffCaps.get(peerId); }

  private mobileFor(peerId: string): MobileRecoveryBinding {
    let binding = this.mobileBindings.get(peerId);
    if (binding) return binding;
    binding = new MobileRecoveryBinding({ peerId, peerGeneration: ++this.bindingGeneration, nowMs: this.nowMs,
      currentTier: () => this.deps.getCurrentTier?.(peerId) ?? 'middle',
      configuredFps: () => this.deps.getCurrentFps?.(peerId) ?? TARGET_FRAMERATE,
      currentCap: () => this.getSnapshot(peerId).maxBitrateBps,
      hardCap: () => hardEpisode(this.cutContextFor(peerId), this.nowMs()).maxBitrateBps,
      bitrateApplied: () => !this.episodes.isNormal(peerId),
      protectBitrate: (strength) => {
        if (!this.episodes.isNormal(peerId)) return;
        if (strength === 'hard') this.entry.enterOrEscalate(peerId, 'sustained-poor-playback', true);
        else this.entry.enterElevated(peerId);
        this.syncBitrateCutEligibility(peerId);
      },
      setHandoffCap: cap => this.setHandoffProtection(peerId, cap),
      resetPath: epoch => this.resetPathEvidence(peerId, epoch),
      demote: this.deps.applyCapacityTierDemotion,
      resolutionFallbackUnavailableReason: this.deps.resolutionFallbackUnavailableReason,
      emit: event => this.emitHealth(peerId, event) });
    this.mobileBindings.set(peerId, binding);
    return binding;
  }

  private syncBitrateCutEligibility(peerId: string): void {
    if (this.episodes.isNormal(peerId)) this.capacity.clearBitrateCut(peerId);
    else this.capacity.noteBitrateCut(peerId);
  }

  private invalidatePlaybackEvidence(peerId: string): void {
    this.climb.invalidateEvidence(peerId);
    this.reclaim?.invalidateEvidence(peerId);
  }

  /** A manager with no calibration source cannot wait for one; with one, the floor must be trusted. */
  private calibratedFloorTrusted(peerId: string): boolean {
    if (!this.deps.getCalibratedLatency) return true;
    const floorMs = this.deps.getCalibratedLatency(peerId)?.floorMs;
    return typeof floorMs === 'number' && Number.isFinite(floorMs);
  }

  /** D2: the latency-first correction — a soft cut that then holds, sticky (D1). */
  private correctLatencyBreach(peerId: string): void {
    this.entry.raiseStickyFloor(peerId, 'soft', 'latency-budget-breach');
    if (this.episodes.isNormal(peerId)) this.entry.enterElevated(peerId);
  }

  /** A for-cause floor rose: record it and tell the reclaim manager (oscillation clock). */
  private onFloorRaised(peerId: string, floor: SessionDowngradeFloor, reason: string): void {
    this.reclaim?.noteFloorRaised(peerId);
    this.emitHealth(peerId, { kind: 'downgrade', stream: 'video', atMs: this.nowMs(), detail: { floor, reason } });
  }

  /** The reclaim manager released a floor: record it and re-prime the peer at the new level. */
  private onFloorReleased(peerId: string, from: SessionDowngradeFloor, to: SessionDowngradeFloor, reason: string): void {
    this.emitHealth(peerId, { kind: 'reclaim', stream: 'video', atMs: this.nowMs(), detail: { from, to, reason } });
    if (from === 'tier360' && this.deps.applyCapacityTierPromotion) {
      void this.deps.applyCapacityTierPromotion(peerId).catch(() => {
        this.deps.stickyDowngrade?.raise(peerId, 'tier360', 'reclaim-effect-failed');
        this.restoreStickyFloorHold(peerId);
        this.debug('[Recovery.reclaim]', { peerId, outcome: 'resolution-effect-failed' });
      });
    }
    if (!this.climb.rebaseAfterRelease(peerId) && to !== 'none') this.restoreStickyFloorHold(peerId);
  }

  private rememberLink(peerId: string, sample: HostWindowLinkSample): void {
    this.lastLink.set(peerId, sample);
  }

  private releaseWindowCut(peerId: string, now: number): void {
    const episode = this.episodes.get(peerId);
    if (!episode || episode.state === 'normal') return;
    if (!this.windowGuard.cutOpenedWithTransition(episode.enteredAt, now)) return;
    if (linkProvesCongestion(this.lastLink.get(peerId))) return;
    this.episodes.end(peerId, {
      recoveryCleared: true,
      clearedAtMs: now,
      lastRecoveryMaxBitrateBps: episode.maxBitrateBps,
    });
    const raisedAt = this.deps.stickyDowngrade?.stateFor(peerId).appliedAtMs;
    if (raisedAt !== undefined && this.windowGuard.cutOpenedWithTransition(raisedAt, now)) {
      this.deps.stickyDowngrade?.release(peerId, 'none', 'host-window-transition');
    }
    const restored = this.getSnapshot(peerId);
    this.deps.applyEncoding(peerId, { maxBitrateBps: restored.maxBitrateBps, maxFramerate: restored.maxFramerate });
    this.capacity.clearBitrateCut(peerId);
    this.debug('[Recovery.window]', { peerId, restoredBitrateBps: restored.maxBitrateBps });
  }

  private cutContextFor(peerId: string): RecoveryCutContext {
    return {
      profileMaxBitrateBps: this.deps.getProfileMaxBitrateBps(peerId),
      encodeContext: this.encodeContext(peerId),
      fpsCeiling: this.fpsCeilingForPeer(peerId),
    };
  }

  private stickyFloorFor(peerId: string): SessionDowngradeFloor {
    return this.deps.stickyDowngrade?.floorFor(peerId) ?? 'none';
  }

  /** Effective fps ceiling for climb / priming (never above sticky ceiling or profile max). */
  private fpsCeilingForPeer(peerId: string): number {
    return this.deps.stickyDowngrade?.fpsCeilingFor(peerId, TARGET_FRAMERATE) ?? TARGET_FRAMERATE;
  }

  private stickyFloorEncodingFor(peerId: string): StickyFloorEncoding {
    return stickyFloorEncodingFor(this.stickyFloorFor(peerId), this.cutContextFor(peerId), this.episodes.get(peerId)?.maxBitrateBps);
  }

  /** Live encode context for this peer, or undefined when it cannot be determined. */
  private encodeContext(peerId: string): StreamEncodeContext | undefined {
    try {
      return this.deps.getEncodeContext?.(peerId);
    } catch {
      return undefined; // A capture query that throws must not take the congestion response down.
    }
  }

  /**
   * The framerate the encoder actually put on the wire for this peer — the ceiling on what the
   * guest could possibly decode. Falls back to the configured rate (the lowest of the
   * negotiated send fps, the sticky ceiling and the live episode's cap) before the first
   * measurement lands.
   */
  private deliveredFpsFor(peerId: string): number {
    const measured = this.deps.getDeliveredFps?.(peerId);
    if (typeof measured === 'number' && Number.isFinite(measured) && measured > 0) return measured;
    const candidates = [this.deps.getCurrentFps?.(peerId), this.fpsCeilingForPeer(peerId), this.episodes.get(peerId)?.maxFramerate].filter(
      (fps): fps is number => typeof fps === 'number' && Number.isFinite(fps) && fps > 0,
    );
    return candidates.length > 0 ? Math.min(...candidates) : TARGET_FRAMERATE;
  }

  /** Emit a health event onto the observability bus (no-op when the bus is not wired). */
  private emitHealth(peerId: string, event: StreamHealthEvent): void {
    try {
      this.deps.onHealthEvent?.(peerId, event);
    } catch {
      // The observability bus must never take the congestion response down with it.
    }
  }

  /** DEBUG BUILD ONLY — console + the webrtc debug sink, gated live. */
  private debug(tag: string, line: Record<string, unknown>): void {
    if (!isDebugWebrtcLoggingEnabled()) return;
    console.log(tag, line);
    sinkWebrtcDebugLine(JSON.stringify({ t: new Date().toISOString(), tag, ...line }));
  }
}
